import { linkOptions } from '@tanstack/react-router';
import { useUserRoles, useReportPermissions, useAdvertiserPermissions, useEngagementPermissions } from '@anytag/auth';
import {
  Engagement,
  EngagementCampaignStatus,
  namedOperations,
  useUpdateEngagementStatusMutation
} from '@anytag/graphql';
import { TableCell } from '@anytag/shared-atoms';
import { UNEXPECTED_ERROR } from '@anytag/shared-constants';
import { useQueryHelper, useTranslateCountry } from '@anytag/shared-hooks';
import { SpreadButton } from '@anytag/shared-molecules';
import { formatNumber } from '@anytag/shared-utils';
import { ColumnDef } from '@tanstack/react-table';
import { EngagementStatus } from '../../EngagementStatus';

interface Props {
  onDeleteEngagement: (egg: { id: number; name: string }) => void;
}

export const useEngagementCampaignsTable = ({ onDeleteEngagement }: Props) => {
  const { tc } = useTranslateCountry();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { hideReportLink } = useReportPermissions();
  const { hideEditAdvertiserBtn } = useAdvertiserPermissions();
  const { hideEditEngagementBtn, hideDeleteEngagementBtn } = useEngagementPermissions();
  const { isAdvertiser, isAdminStaff, isAdminStaffAgency, isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { callUpdateEngagementStatus, loading } = useUpdateEngagementStatusMutation({
    refetchQueries: [namedOperations.Query.AllEngagements]
  });

  const handleUpdateEngagementStatus = async ({ id, status }: { id: number; status: EngagementCampaignStatus }) => {
    try {
      await callUpdateEngagementStatus({ variables: { input: { id, status } } });
    } catch (error) {
      enqueueSnackbar(t(error.message || UNEXPECTED_ERROR), { variant: 'error' });
    }
  };

  const columns: ColumnDef<Engagement>[] = [
    {
      accessorKey: 'title',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Campaign Title'),
      cell: ({ row }) => {
        const { id, status, title } = row.original;
        const campaignId = id.toString();
        const proposalPageLink =
          // Advertisers can't edit Proposal for the DRAFT campaigns
          isAdvertiser && status === EngagementCampaignStatus.DRAFT
            ? linkOptions({ to: '/engagement/$id', params: { id: campaignId } })
            : linkOptions({ to: '/engagement/$id/proposal', params: { id: campaignId } });

        return (
          <div className="campaign-title-column">
            <TableCell.Link label={row.getValue('title')} {...proposalPageLink} />
            <SpreadButton
              collapseFrom={2}
              options={[
                {
                  icon: 'post',
                  label: t('Posts'),
                  tooltip: { help: t('Button.Posts') },
                  to: '/engagement/$id/posts',
                  params: { id: campaignId },
                  disabled: [EngagementCampaignStatus.DRAFT, EngagementCampaignStatus.DEMO].includes(status)
                },
                {
                  icon: 'chart',
                  label: t('Report'),
                  hidden: hideReportLink,
                  tooltip: { help: t('Button.Report') },
                  to: '/engagement/$id/report',
                  params: { id: campaignId },
                  disabled: [
                    EngagementCampaignStatus.DRAFT,
                    EngagementCampaignStatus.REVIEWING,
                    EngagementCampaignStatus.UPCOMING,
                    EngagementCampaignStatus.DEMO
                  ].includes(status)
                },
                {
                  icon: 'edit',
                  label: t('Edit'),
                  hidden: hideEditEngagementBtn,
                  to: '/engagement/$id',
                  params: { id: campaignId }
                },
                {
                  icon: 'delete',
                  label: t('Delete'),
                  hidden: hideDeleteEngagementBtn,
                  onClick: () => onDeleteEngagement({ id, name: title }),
                  disabled: !isAdminStaff || status === EngagementCampaignStatus.FINISHED
                },
                {
                  icon: 'flag',
                  hidden: !isAdminStaff,
                  label: t('Button.Mark as Lost'),
                  onClick: () => handleUpdateEngagementStatus({ id, status: EngagementCampaignStatus.LOST }),
                  disabled:
                    loading || ![EngagementCampaignStatus.REVIEWING, EngagementCampaignStatus.DRAFT].includes(status)
                },
                {
                  icon: 'flag',
                  hidden: !isAdminStaff,
                  label: t('Button.Mark as Draft'),
                  disabled: loading || status !== EngagementCampaignStatus.LOST,
                  onClick: () => handleUpdateEngagementStatus({ id, status: EngagementCampaignStatus.DRAFT })
                }
              ]}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'countries',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => (
        <TableCell.Text
          value={row.original.countries
            .filter((el) => el !== null)
            .map(({ name }) => tc(name))
            .join(', ')}
        />
      )
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Status'),
      cell: ({ row }) => <EngagementStatus status={row.original.status} />
    },
    {
      accessorKey: 'advertiser',
      meta: { hidden: !isAdminStaffAgency },
      header: t<string>('HeaderColumn.Advertiser'),
      cell: ({ row }) => (
        <TableCell.Link
          to="/advertiser/$id"
          disabled={hideEditAdvertiserBtn}
          label={row.original.advertiser.name}
          params={{ id: row.original.advertiser.id.toString() }}
        />
      )
    },
    {
      accessorKey: 'dateRange',
      header: t<string>('HeaderColumn.Date Range'),
      cell: ({ row }) => (
        <TableCell.Period period={{ startDate: row.original.startDate, endDate: row.original.endDate }} />
      )
    },
    {
      accessorKey: 'budget',
      meta: { align: 'right' },
      header: t<string>('HeaderColumn.Budget'),
      cell: ({ row }) => (
        <TableCell.Number
          value={formatNumber(row.original.budget)}
          unit={row.original.currency ? t(row.original.currency) : undefined}
        />
      )
    },
    {
      accessorKey: 'salesPics',
      meta: { hidden: !isAdminStaffTalentAgencyPartner },
      header: t<string>('HeaderColumn.Sales PIC'),
      cell: ({ row }) => (
        <TableCell.Text
          value={row.original.salesPics
            ?.slice(0, 2)
            .map((pic) => pic.name)
            .join(', ')}
        />
      )
    },
    {
      accessorKey: 'influencerManagementPics',
      meta: { hidden: !isAdminStaffTalentAgencyPartner },
      header: t<string>('HeaderColumn.Influencer Management PIC'),
      cell: ({ row }) => (
        <TableCell.Text
          value={row.original.influencerManagementPics
            ?.slice(0, 2)
            .map((pic) => pic.name)
            .join(', ')}
        />
      )
    }
  ];

  return { columns };
};
